import { Link } from "react-router-dom";
import { MessageCircle, Sparkles, Compass, Stars, BookOpen, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import UrgencyCard from "@/components/UrgencyCard";
import { WhatsAppQRModal } from "@/components/ChatBot";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";

const services = [
  { icon: Stars, name: "Astrología", key: "landing_service_astrology_desc" },
  { icon: Sparkles, name: "Numerología", key: "landing_service_numerology_desc" },
  { icon: Compass, name: "Tarot intuitivo", key: "landing_service_tarot_desc" },
];

// Portada oficial del libro (URL proporcionada por Bea)
const bookImg = "https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEik0isas39Y6uLMVI5pxLDFARcXKiZCL7_6U5x0E_SAi4a0xfmKRfe0pFkli6L86ZJ3kKN3Us9XroHj1BOHqIdRp6IYKQZA8E1HG2Q9tzjpBMbv-hM2VX6fkOTsm_GQOMAX0MaK0eE9BuB-Vlj6CMlDOyRbYrQivn6Buh9j2p7Grx3GAD7bPKPEjrfW2F8/s16000/Bea%20de%20Ibiza%20autora%20libro%20un%20lenguaje%20invisible.jpg";

export default function Landing() {
  const [showQR, setShowQR] = useState(false);
  // Ronda 32.5: control «Leer más / Leer menos» por tarjeta de servicio.
  // Estado como Set de índices expandidos — evita renders innecesarios de
  // las otras tarjetas y mantiene el diseño uniforme cuando ninguna está
  // abierta.
  const [expandedServices, setExpandedServices] = useState(() => new Set());
  const toggleService = (i) => {
    setExpandedServices((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };
  const [texts, setTexts] = useState({});
  useEffect(() => {
    api.get("/config/public")
      .then(({ data }) => setTexts(data?.content_texts || {}))
      .catch(() => setTexts({}));
  }, []);
  const t = (key, fallback) => (texts && texts[key]) || fallback;
  return (
    <div className="relative">
      <WhatsAppQRModal open={showQR} onClose={() => setShowQR(false)} />
      {/* Header */}
      <header className="glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif text-3xl md:text-4xl text-[#0A0A0A] font-medium" data-testid="brand-link">Bea de Ibiza</Link>
          <div className="flex items-center gap-5">
            <a href="#quien-soy" data-testid="header-quien-link" className="hidden md:inline text-sm text-[#3B342A] hover:text-[#B8912E] transition-colors">
              Quién soy
            </a>
            <Link to="/reservar" data-testid="landing-reserve-cta" className="btn-primary text-sm">
              Reservar mi cita
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="astral-bg"></div>
        <div className="astral-bloom"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-24 md:py-36 grid md:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="md:col-span-8"
          >
            <span className="label-eyebrow" data-testid="hero-eyebrow">{t("landing_hero_eyebrow", "Consultas · Astrología · Numerología · Tarot")}</span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light mt-5 text-[#141210] leading-[1.05]">
              {t("landing_hero_headline_lead", "Existe un")} <span className="gold italic">{t("landing_hero_headline_gold", "lenguaje invisible")}</span>
              <br/>
              {t("landing_hero_headline_tail", "y sé leerlo contigo.")}
            </h1>
            <p className="mt-7 text-[#3B342A] max-w-xl text-base md:text-lg leading-relaxed whitespace-pre-line" data-testid="hero-intro">
              {t("landing_hero_intro", "Cuando la astrología, la numerología y el tarot coinciden entre sí, es que algo se va a dar. Ese es mi método: escuchar las tres voces y traducir lo que dicen para ti.")}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/reservar" data-testid="hero-reserve-btn" className="btn-primary">Reservar cita</Link>
              <Link to="/reservar" data-testid="hero-manage-btn" className="btn-ghost">Gestionar mi cita</Link>
              <a href="#servicios" data-testid="hero-learn-more" className="btn-ghost">Saber más</a>
            </div>
          </motion.div>
          <div className="md:col-span-4">
            <div className="card-gold">
              <div className="label-eyebrow">La consulta</div>
              <ul className="mt-5 space-y-2.5 text-sm text-[#141210]" data-testid="consultation-bullets">
                {t("landing_consultation_bullets", "Consulta telefónica\nDirectamente con Bea\nPersonal, concreta y confidencial\nPago en el momento de la reserva\nConfirmación al instante")
                  .split("\n").filter(Boolean).map((line, i) => (
                    <li key={`bullet-${i}`}>· {line}</li>
                  ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                <div className="label-eyebrow">Cómo contactar</div>
                <div className="text-sm">
                  <div className="text-[#141210]">Cambiar o cancelar tu cita</div>
                  <div className="text-[#8A8175] text-xs mt-0.5">Con tu teléfono, en esta web</div>
                </div>
                <div className="text-sm">
                  <div className="text-[#141210]">Dudas antes de la cita</div>
                  <div className="text-xs text-[#6C6355]">
                    Pregúntale al chat de esta web (esquina inferior).
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Urgencia — inmediatamente debajo del hero */}
      <section className="max-w-6xl mx-auto px-6 pt-2 pb-10 relative z-10">
        <UrgencyCard variant="landing"/>
      </section>

      {/* Services */}
      <section id="servicios" className="max-w-6xl mx-auto px-6 py-24 relative z-10">
        <span className="label-eyebrow" data-testid="services-eyebrow">{t("landing_services_eyebrow", "Tres voces, un mismo mensaje")}</span>
        <h2 className="font-serif text-3xl md:text-4xl font-light mt-3 max-w-2xl text-[#141210]">
          {t("landing_services_heading_lead", "Herramientas ancestrales para leer")} <span className="gold italic">{t("landing_services_heading_gold", "tu momento presente")}</span>.
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {services.map((s, i) => {
            const isExpanded = expandedServices.has(i);
            const desc = t(s.key, "");
            return (
              <motion.div
                key={s.name}
                whileHover={{ y: -4 }}
                className="card card-hover flex flex-col"
                data-testid={`service-${s.name.toLowerCase()}`}
              >
                <s.icon className="gold" size={26} />
                <h3 className="font-serif text-2xl mt-4 text-[#141210]">{s.name}</h3>
                <p
                  className={`text-[#6C6355] mt-2 text-sm leading-relaxed transition-all duration-300 ${isExpanded ? "" : "line-clamp-3"}`}
                  data-testid={`service-${s.name.toLowerCase()}-desc`}
                >
                  {desc}
                </p>
                <button
                  type="button"
                  onClick={() => toggleService(i)}
                  className="gold text-xs font-medium mt-3 self-start hover:underline transition-colors"
                  data-testid={`service-${s.name.toLowerCase()}-toggle`}
                >
                  {isExpanded ? "Leer menos ↑" : "Leer más →"}
                </button>
              </motion.div>
            );
          })}
        </div>

      </section>

      {/* Quién es Bea de Ibiza */}
      <section id="quien-soy" className="max-w-6xl mx-auto px-6 py-24 relative z-10">
        <div className="divider-gold mb-12"></div>
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="label-eyebrow">Quién soy</span>
            <h2 className="font-serif text-3xl md:text-4xl font-light mt-3 text-[#0A0A0A] leading-tight">
              <span className="gold italic">Bea de Ibiza</span><br/>
              en primera persona.
            </h2>
            <p className="text-[#6C6355] text-sm mt-6 leading-relaxed" data-testid="about-subtitle">
              {t("landing_about_subtitle", "De alumna inesperada a maestra por vocación. Autora de «Un Lenguaje Invisible».")}
            </p>
          </div>
          <div className="md:col-span-8 text-[#3B342A] leading-relaxed text-base md:text-lg space-y-5">
            {/* Ronda 32.1: cita literaria destacada del libro, con comillas grandes,
                cursiva y atribución. Se separa del cuerpo para que se lea como
                epígrafe (no como texto normal). */}
            <figure
              className="relative my-2 pl-6 md:pl-10 py-4 border-l-4 border-[#B8912E]/70"
              data-testid="about-quote-block"
            >
              <span
                aria-hidden="true"
                className="absolute -top-2 left-1 md:left-3 text-6xl md:text-7xl leading-none font-serif text-[#B8912E]/40 select-none"
              >“</span>
              <blockquote
                className="font-serif italic text-2xl md:text-3xl text-[#141210] leading-snug"
                data-testid="about-quote"
              >
                {t("landing_about_quote", "No busqué este camino, apareció.")}
              </blockquote>
              <figcaption
                className="mt-3 text-sm text-[#6C6355] tracking-wide"
                data-testid="about-quote-attribution"
              >
                — <em>{t("landing_about_quote_attribution", "Un Lenguaje Invisible")}</em>
              </figcaption>
            </figure>
            <p data-testid="about-p1">
              {t("landing_about_p1", "Todo empezó a finales de los años noventa, cuando llegué a Ibiza y me crucé con una psíquica inglesa que, desde el primer encuentro, me miró y me dijo: «tú eres psíquica como yo». Me pidió que la acompañara como traductora en sus consultas y, sin darme cuenta, aquella colaboración se convirtió en la formación más honda que he recibido nunca.")}
            </p>
            <p data-testid="about-p2">
              {t("landing_about_p2", "Pasaba horas a su lado, traduciendo y observando, pero lo importante llegaba al terminar cada sesión. Ella me preguntaba: «¿tú qué has visto ahí? ¿por qué crees que he dicho esto?». Aquellas preguntas, más que cualquier manual, fueron mi verdadera escuela. Con el tiempo seguí formándome en distintos lugares del mundo y acabé colaborando en radio y televisión, acompañando a miles de personas en su propio proceso de autoconocimiento.")}
            </p>
            <p data-testid="about-p3">
              {t("landing_about_p3", "El nombre «Bea de Ibiza» no lo elegí yo: me lo puso la gente. Cuando me fui de la isla, empezaron a preguntar en internet «¿dónde está Bea de Ibiza?», «¿qué ha sido de la astróloga de Ibiza?». Ese apodo se quedó y, aunque he vivido en muchos sitios, lo mantengo como un guiño a mis raíces y a las personas que me han seguido desde entonces.")}
            </p>
            <p data-testid="about-p4">
              {t("landing_about_p4", "Trabajo con astrología predictiva, numerología pitagórica y tarot intuitivo, tejidos siempre con la intuición como hilo conductor. Todo lo que he ido aprendiendo en más de tres décadas está reunido en mi libro «Un Lenguaje Invisible», un método pensado para que cualquiera pueda entrenar su propia escucha.")}
            </p>
            <p className="text-[#6C6355] italic pt-2 border-t border-[#B8912E]/25" data-testid="about-gratitude">
              {t("landing_about_gratitude", "Mi agradecimiento a quienes me enseñaron sin pedir nada a cambio, a mis consultantes por confiarme su historia, y a mi mentora, cuyo legado me recuerda cada día que la intuición se entrena y se comparte.")}
            </p>
            <div className="pt-4">
              <a href="https://unlenguajeinvisible.com" target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2 text-sm" data-testid="about-book-cta">
                <BookOpen size={14}/> Leer <em>Un Lenguaje Invisible</em>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* About - book (Mi enfoque) */}
      <section className="max-w-6xl mx-auto px-6 py-24 grid md:grid-cols-12 gap-10 items-center relative z-10">
        <div className="md:col-span-6 order-2 md:order-1">
          <span className="label-eyebrow">Mi enfoque</span>
          <h2 className="font-serif text-3xl md:text-4xl font-light mt-3 text-[#0A0A0A]">
            {t("landing_approach_heading", "Intuición, señales, causalidad.")}
          </h2>
          <p className="text-[#3B342A] mt-5 leading-relaxed text-lg whitespace-pre-line">
            {t("landing_approach_intro", "No existe la casualidad, sino la causalidad. Todo lo que aparece en una consulta —una fecha, un número, una carta que se repite— habla. Mi trabajo es traducirlo contigo.")}
          </p>

          <div className="mt-6 space-y-4 text-[#3B342A] leading-relaxed">
            <div>
              <div className="label-eyebrow text-[#B8912E]">Astrología predictiva</div>
              <p className="mt-2 text-base whitespace-pre-line" data-testid="approach-astrology">
                {t("landing_approach_astrology", "Trabajo tu carta natal como mapa de fondo, y sobre ella leo los tránsitos que están sonando ahora, la revolución solar (el año que va de tu cumpleaños al siguiente), las sinastrías de pareja —cómo os leéis dos cartas juntas— y las cartas progresadas, que muestran la vida completa de una persona proyectada en años concretos.")}
              </p>
            </div>
            <div>
              <div className="label-eyebrow text-[#B8912E]">Numerología pitagórica</div>
              <p className="mt-2 text-base whitespace-pre-line" data-testid="approach-numerology">
                {t("landing_approach_numerology", "Los números que estructuran tu camino: cifras personales, ciclos, años y meses que te están tocando. Cuándo empujar, cuándo dejar descansar.")}
              </p>
            </div>
            <div>
              <div className="label-eyebrow text-[#B8912E]">Tarot intuitivo</div>
              <p className="mt-2 text-base whitespace-pre-line" data-testid="approach-tarot">
                {t("landing_approach_tarot", "Una lectura directa, sin adornos, de lo que hoy quiere manifestarse. Las cartas como espejo del momento presente, no como oráculo.")}
              </p>
            </div>
            <div>
              <div className="label-eyebrow text-[#B8912E]">Intuición</div>
              <p className="mt-2 text-base whitespace-pre-line" data-testid="approach-intuition">
                {t("landing_approach_intuition", "Más de 30 años de intuición desarrollada tejida con las tres disciplinas anteriores. Cuando las tres voces coinciden entre sí y mi intuición coincide con ellas, es señal clara de que algo se va a dar. Ese cruce —cuatro voces afinando la misma nota— es el corazón de mi método.")}
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/reservar" data-testid="about-reserve-btn" className="btn-primary">Reservar mi cita</Link>
            <a href="https://unlenguajeinvisible.com" target="_blank" rel="noreferrer" data-testid="book-link" className="btn-ghost inline-flex items-center gap-2">
              <BookOpen size={16}/> Comprar el libro
            </a>
          </div>
        </div>
        <div className="md:col-span-6 order-1 md:order-2">
          <a href="https://unlenguajeinvisible.com" target="_blank" rel="noreferrer" data-testid="book-cover-link" className="block group">
            <div className="relative rounded-2xl overflow-hidden border-2 border-[#B8912E]/40 bg-white aspect-square flex items-center justify-center shadow-[0_16px_50px_rgba(184,145,46,0.16)]">
              <img
                src={bookImg}
                alt="Bea de Ibiza, autora del libro Un Lenguaje Invisible"
                data-testid="book-cover-image"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          </a>
          <p className="text-center text-[#0A0A0A] text-base md:text-lg mt-5 font-serif italic font-medium" data-testid="book-caption">
            {t("landing_book_caption", "«Un Lenguaje Invisible» — Método Bea de Ibiza. Adquiere tu ejemplar en Amazon.")}
          </p>
        </div>
      </section>

      {/* Feng Shui - separate */}
      <section id="feng-shui" className="max-w-6xl mx-auto px-6 py-16 relative z-10">
        <div className="divider-gold mb-16"></div>
        <div className="card border-[#B8912E]/20">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <span className="label-eyebrow">Servicio aparte · Feng Shui</span>
              <h3 className="font-serif text-2xl md:text-3xl font-light mt-3 text-[#141210]" data-testid="fengshui-heading">
                {t("landing_fengshui_heading", "¿Buscas armonizar tu hogar con Feng Shui?")}
              </h3>
              <p className="text-[#3B342A] mt-4 leading-relaxed whitespace-pre-line" data-testid="fengshui-body">
                {t("landing_fengshui_body", "El Feng Shui es un servicio gestionado por separado y no forma parte de este calendario de consultas. Si te interesa una asesoría o cita específica de Feng Shui, escríbeme por WhatsApp y te doy los detalles del proceso aparte.")}
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <button
                onClick={() => setShowQR(true)}
                data-testid="fengshui-whatsapp"
                className="btn-primary text-sm inline-flex items-center gap-2"
              >
                <MessageCircle size={14}/> Info Feng Shui
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-10 relative z-10">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row justify-between gap-4 text-sm text-[#8A8175]">
          <div>© {new Date().getFullYear()} {t("landing_footer_credit", "Bea de Ibiza · Todos los derechos reservados")}</div>
          <div className="flex flex-wrap gap-6">
            <a href="https://unlenguajeinvisible.com" target="_blank" rel="noreferrer" className="hover:text-[#B8912E] transition-colors inline-flex items-center gap-1" data-testid="footer-book">
              Libro <ExternalLink size={12}/>
            </a>
            <Link to="/privacidad" className="hover:text-[#B8912E] transition-colors" data-testid="footer-privacy-link">
              Política de privacidad
            </Link>
            <span className="text-[#8A8175]">
              Dudas: chat de esta web
            </span>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-8 -mt-4" data-testid="privacy-notice">
          <p className="text-[11px] leading-relaxed text-[#8A8175]">
            {t("landing_privacy_notice", "Guardamos tu conversación durante 12 meses para poder ayudarte mejor. Tus datos están protegidos conforme al RGPD.")}
          </p>
        </div>
      </footer>
    </div>
  );
}
